import React, { useEffect, useMemo, useState } from 'react';
import { LoadingIndicator, Notice, Pill } from '../components/status';
import { usePortalContext } from '../context/PortalContext';

type CustomerProfile = {
  profileId: string;
  subscriptionId: string;
  billingTenantId: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  industry: string | null;
  companySize: string | null;
  country: string | null;
  onboardingStatus: 'pending' | 'activated' | 'complete';
};

type OnboardingStep = {
  key: string;
  label: string;
  complete: boolean;
  required: boolean;
};

type ResolvedToken = {
  marketplaceSubscriptionId: string;
  subscriptionId: string;
  billingTenantId: string;
  planCode: string;
  offerId: string;
  status: string;
  purchaserEmail?: string;
  quantity?: number | null;
  source?: string;
};

const INDUSTRIES = [
  { value: '', label: 'Select industry (optional)' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'energy', label: 'Energy & Utilities' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial', label: 'Financial Services' },
  { value: 'government', label: 'Government & Public Sector' },
  { value: 'technology', label: 'Technology' },
  { value: 'education', label: 'Education' },
  { value: 'transport', label: 'Transport & Logistics' },
  { value: 'other', label: 'Other' }
];

const COMPANY_SIZES = [
  { value: '', label: 'Select company size (optional)' },
  { value: '1-50', label: '1–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-1000', label: '201–1,000 employees' },
  { value: '1000+', label: '1,000+ employees' }
];

const stepStatus = (step: OnboardingStep): { label: string; kind: 'success' | 'warning' | 'neutral' } => {
  if (step.complete) return { label: 'Complete', kind: 'success' };
  if (step.required) return { label: 'Required', kind: 'warning' };
  return { label: 'Optional', kind: 'neutral' };
};

const parseApiError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = (await response.json()) as { message?: string; error?: string };
    return payload.message || payload.error || fallback;
  } catch {
    return fallback;
  }
};

const Activate = (): JSX.Element => {
  const { tenant } = usePortalContext();

  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [resolvedToken, setResolvedToken] = useState<ResolvedToken | null>(null);
  const [resolvingToken, setResolvingToken] = useState(false);
  const [tokenMessage, setTokenMessage] = useState<string | null>(null);

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [country, setCountry] = useState('');

  const token = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  }, []);

  const loadProfile = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const response = await fetch('/api/onboarding/profile');
      if (!response.ok) {
        throw new Error(await parseApiError(response, `Profile request failed (${response.status})`));
      }
      const data = (await response.json()) as {
        profile?: CustomerProfile | null;
        steps?: OnboardingStep[];
      };
      const loadedProfile = data.profile ?? null;
      setProfile(loadedProfile);
      setSteps(Array.isArray(data.steps) ? data.steps : []);

      if (loadedProfile) {
        setCompanyName(loadedProfile.companyName || '');
        setContactName(loadedProfile.contactName || '');
        setContactEmail(loadedProfile.contactEmail || '');
        setContactPhone(loadedProfile.contactPhone || '');
        setIndustry(loadedProfile.industry || '');
        setCompanySize(loadedProfile.companySize || '');
        setCountry(loadedProfile.country || '');
      } else {
        setCompanyName('');
        setContactName('');
        setContactEmail(tenant?.userEmail ?? '');
        setContactPhone('');
        setIndustry('');
        setCompanySize('');
        setCountry('');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.userEmail]);

  useEffect(() => {
    const resolveToken = async (): Promise<void> => {
      if (!token) return;
      setResolvingToken(true);
      setTokenMessage(null);
      try {
        const response = await fetch('/api/onboarding/resolve-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        if (!response.ok) {
          throw new Error(await parseApiError(response, `Token resolve failed (${response.status})`));
        }
        const data = (await response.json()) as ResolvedToken;
        setResolvedToken(data);
        setTokenMessage(`Token resolved for plan '${data.planCode}'.`);
        if (data.purchaserEmail) {
          setContactEmail((current) => current || data.purchaserEmail || '');
        }
      } catch (err) {
        setResolvedToken(null);
        setTokenMessage((err as Error).message);
      } finally {
        setResolvingToken(false);
      }
    };
    void resolveToken();
    // Resolve once per token. Depending on contactEmail here re-posted the
    // token on every keystroke and tripped the 10/min limit (tracker #114).
  }, [token]);

  const submit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);

    const payload = {
      // The raw landing token lets the server re-resolve the purchase and
      // bind activation to the buying tenant authoritatively.
      token: token || undefined,
      marketplaceSubscriptionId: resolvedToken?.marketplaceSubscriptionId || resolvedToken?.subscriptionId,
      marketplacePlanId: resolvedToken?.planCode,
      marketplaceQuantity: resolvedToken?.quantity,
      companyName,
      contactName,
      contactEmail,
      contactPhone,
      industry,
      companySize,
      country
    };

    try {
      // A landing-page token always goes through activation, even when a
      // profile already exists: a second purchase, a repeat visit from
      // Microsoft's Manage link, or a retry after a failed activation must
      // reach Microsoft's activate call (tracker #114 P2-2). Only a plain
      // profile edit (no token) PATCHes the profile.
      const activating = Boolean(token) || !profile;
      const endpoint = activating ? '/api/onboarding/activate' : '/api/onboarding/profile';
      const method = activating ? 'POST' : 'PATCH';
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(await parseApiError(response, `Save request failed (${response.status})`));
      }
      await loadProfile();
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const hasInstances = steps.some((step) => step.key === 'firstInstall' && step.complete);
  const hasFirstAudit = steps.some((step) => step.key === 'firstAudit' && step.complete);

  // Clear the "Saved" pill as soon as the user changes any field after a save,
  // so it does not falsely imply later edits are also saved. The guard avoids
  // an unnecessary re-render on initial load.
  useEffect(() => {
    if (saved) setSaved(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, contactName, contactEmail, contactPhone, industry, companySize, country]);

  const completedStepsCount = steps.filter((s) => s.complete).length;
  const requiredStepsRemaining = steps.filter((s) => s.required && !s.complete).length;

  return (
    <div className="activate-page">
      <div className="page-hero">
        <h2>Activate Edinshall Audit</h2>
        <p className="hero-intro">
          Set up your customer profile and see the onboarding steps still left to complete.
        </p>
        {steps.length > 0 && (
          <div className="hero-chips">
            <span className="hero-chip"><strong>{completedStepsCount}</strong> of {steps.length} steps complete</span>
            {requiredStepsRemaining > 0 && (
              <span className="hero-chip"><strong>{requiredStepsRemaining}</strong> required step{requiredStepsRemaining === 1 ? '' : 's'} remaining</span>
            )}
            {resolvedToken?.planCode && (
              <span className="hero-chip"><strong>Plan</strong> {resolvedToken.planCode}</span>
            )}
          </div>
        )}
      </div>

      {token && (
        <div className="dashboard-card">
          <h3>Marketplace activation</h3>
          <p className="card-intro">Verifying your Microsoft Marketplace subscription.</p>
          {resolvingToken && <LoadingIndicator label="Resolving subscription…" />}
          {!resolvingToken && tokenMessage && (
            <Notice kind={resolvedToken ? 'success' : 'warning'} marginTop={8}>
              {tokenMessage}
            </Notice>
          )}
        </div>
      )}

      {loading && <LoadingIndicator label="Loading activation profile…" marginTop={12} />}

      {error && !loading && (
        <div className="dashboard-card">
          <Notice kind="warning">{error}</Notice>
          <button className="btn secondary" type="button" onClick={() => void loadProfile()} style={{ alignSelf: 'flex-start' }}>
            Retry
          </button>
        </div>
      )}

      {!loading && (
        <>
          <form className="dashboard-card" onSubmit={(event) => void submit(event)}>
            <h3>{profile ? 'Update company profile' : 'Complete your company profile'}</h3>
            <p className="card-intro">These details are used to initialize your tenant onboarding.</p>

            <div className="activate-form">
              <label>
                Company Name
                <input className="input field-input" required placeholder="Acme Industries Ltd" value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
              </label>
              <label>
                Contact Name
                <input className="input field-input" required placeholder="Jane Doe" value={contactName} onChange={(event) => setContactName(event.target.value)} />
              </label>
              <label>
                Contact Email
                <input className="input field-input" required type="email" placeholder="jane@example.com" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} />
              </label>
              <label>
                Contact Phone <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span>
                <input className="input field-input" placeholder="+44 …" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
              </label>
              <label>
                Industry
                <select className="input field-input" value={industry} onChange={(event) => setIndustry(event.target.value)}>
                  {INDUSTRIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Company Size
                <select className="input field-input" value={companySize} onChange={(event) => setCompanySize(event.target.value)}>
                  {COMPANY_SIZES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-full">
                Country <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span>
                <input className="input field-input" placeholder="United Kingdom" value={country} onChange={(event) => setCountry(event.target.value)} />
              </label>
            </div>

            <div className="activate-form-footer">
              <button className="btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : profile ? 'Update profile' : 'Activate subscription'}
              </button>
              {saved && <Pill kind="success">Saved</Pill>}
            </div>
          </form>

          <div className="dashboard-card">
            <h3>Getting started</h3>
            <p className="card-intro">Complete these steps to get the most from Edinshall Audit.</p>
            {steps.length === 0 ? (
              <div className="muted">No onboarding steps available yet.</div>
            ) : (
              <ol className="activate-steps">
                {steps.map((step, idx) => {
                  const status = stepStatus(step);
                  const cls = ['activate-step'];
                  if (step.complete) cls.push('is-complete');
                  else if (step.required) cls.push('is-required');
                  return (
                    <li key={step.key} className={cls.join(' ')}>
                      <span className="step-marker" aria-hidden="true">{step.complete ? '✓' : idx + 1}</span>
                      <span className="step-label">{step.label}</span>
                      <Pill kind={status.kind}>{status.label}</Pill>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          <div className="dashboard-card">
            <h3>Next steps</h3>
            {!hasInstances && (
              <p className="card-intro">
                Download the current package from SPFx Releases and deploy it to your SharePoint app catalog,
                or install it there directly from that page. Then add the Edinshall Audit web part to a page.
                Portal deployment rights follow the contact e-mail on this form.
              </p>
            )}
            {hasInstances && !hasFirstAudit && (
              <p className="card-intro">
                Your deployment is live. Open the SharePoint app and create your first audit to begin data capture.
              </p>
            )}
            {hasInstances && hasFirstAudit && (
              <p className="card-intro">
                You are set up. Continue in the dashboard for operational monitoring and configuration.
              </p>
            )}
            <div className="card-actions">
              {!hasInstances && (
                <a className="btn primary" href="/portal/admin/spfx-releases">SPFx Releases</a>
              )}
              <a className="btn secondary" href="/docs">Documentation</a>
              <a className="btn secondary" href="/dashboard">Go to dashboard</a>
              {resolvedToken?.planCode === 'enterprise' && (
                <a className="btn secondary" href="/client-accounts">Manage tenant allocation</a>
              )}
            </div>
            {resolvedToken?.planCode === 'enterprise' && (
              <p className="card-intro">
                Enterprise subscriptions can cover multiple Entra tenants. After activation, assign covered tenant slots in Client Accounts before deploying into additional tenants.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Activate;
